import { useMemo, useState } from "react";
import { Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { GovItemDetail, addItemComment } from "../../../../utils/icGovernance";
import { dateTime } from "../governanceStatus";
import ActionError from "./ActionError";
import SideCard from "./SideCard";
import { FAINT, TEXT, primaryButtonSx } from "./ui";
import { useGovAction } from "./useGovAction";

const time = (iso: string) => new Date(iso).getTime() || 0;

/**
 * Comment thread, oldest first, with a reply box when `permissions.comment`.
 * After posting, the page re-fetches the item (history gains a line too).
 */
const CommentsSection = ({ item, onRefresh }: { item: GovItemDetail; onRefresh: () => Promise<unknown> | void }) => {
  const [reply, setReply] = useState("");
  const action = useGovAction();
  const comments = useMemo(
    () => [...(item.comments ?? [])].sort((a, b) => time(a.createdAt) - time(b.createdAt)),
    [item.comments],
  );

  const post = async () => {
    const content = reply.trim();
    if (!content) return;
    const created = await action.run(() => addItemComment(item.id, content));
    if (created) {
      setReply("");
      await onRefresh();
    }
  };

  return (
    <SideCard title={`Comments (${comments.length})`}>
      {comments.length === 0 ? (
        <Typography variant="body2" sx={{ color: FAINT }}>
          No comments yet.
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {comments.map((c) => (
            <Box
              key={c.id}
              sx={{ border: "1px solid #f0f0f3", borderRadius: "10px", p: 1.5, backgroundColor: "#fcfcfd" }}
            >
              <Typography variant="body2" sx={{ color: FAINT }}>
                <Box component="span" sx={{ fontWeight: 700, color: TEXT }}>
                  {c.author || "Unknown"}
                </Box>{" "}
                · {dateTime(c.createdAt)}
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", mt: 0.5, color: TEXT }}
              >
                {c.content}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      {item.permissions?.comment ? (
        <Box sx={{ mt: 2 }}>
          <TextField
            label="Add a comment"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            disabled={action.busy}
          />
          <ActionError action={action} onReload={() => void onRefresh()} />
          <Box sx={{ textAlign: "right", mt: 1 }}>
            <Button
              variant="contained"
              disableElevation
              onClick={post}
              disabled={!reply.trim() || action.busy}
              startIcon={action.busy ? <CircularProgress size={16} color="inherit" /> : null}
              sx={primaryButtonSx}
            >
              {action.busy ? "Posting…" : "Post comment"}
            </Button>
          </Box>
        </Box>
      ) : null}
    </SideCard>
  );
};

export default CommentsSection;
